import { isFunction } from 'lodash/fp'
import { SetStateAction, useState } from 'react'

import { getLocalStorageItem } from './getLocalStorageItem'
import { LocalStorageKey, LocalStorageValue } from './localStorageSchema'
import { setLocalStorageItem } from './setLocalStorageItem'

/**
 * React hook for reading/writing a typed value from `localStorage`.
 *
 * - Initializes state lazily from `schema validated wrapper of localStorage` (via {@link getLocalStorageItem}).
 * - If nothing is stored under the key, falls back to default value from schema.
 * - The returned setter updates both React state and `localStorage`.
 *
 * @template K - A key from {@link LocalStorageKey}.
 * @param key - Storage key.
 * @returns A tuple of `[value, update]`.
 *
 * @example
 * const [theme, setTheme] = useLocalStorage('theme')
 * setTheme('dark')
 */
export function useLocalStorage<K extends LocalStorageKey>(
  key: K,
): [
  LocalStorageValue<K>,
  (newValue: SetStateAction<LocalStorageValue<K>>) => void,
] {
  const [value, setValue] = useState(() => getLocalStorageItem(key))

  const update = (newValue: SetStateAction<LocalStorageValue<K>>) => {
    const previousValue = getLocalStorageItem(key)
    const nextValue = isFunction(newValue) ? newValue(previousValue) : newValue

    setValue(nextValue)
    setLocalStorageItem(key, nextValue)
  }

  return [value, update]
}
